import { ChangeEvent, FC, Suspense, UIEvent, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { progressBarDashboardStudentTab } from '../../../businessLogicService/progressBarAssignValues';
import { ProgressBar } from '../../../models/ProgressBar';
import SingleUnitProgressBarStudentsProgressTable from '../../SingleUnit/SingleUnitProgressBarStudentsProgressTable';
import QueryStudentsProgressReport, { ProgressTableStudentsValue } from '../QueryStudentsProgressReport';
import { Skill } from '../../../models/SupportSkills';
import QueryStudentsProgressReportSkills from '../QueryStudentsProgressReportSkills';
import { SkillResult } from '../../../models/Skill';
import { PageInfo } from '../../../models/PageInfo';
import { selectedReportsFilter } from '../../../redux/reducers/filterReportsSlice';
import { useAppSelector } from '../../../redux/hooks';
import { formatDateWithTimezone } from '../../../businessLogicService/dateFormatting';
import { selectUser } from '../../../redux/reducers/userSlice';
import ProgressNameColumn from './ProgressColumnName';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import { StudentNameLink } from '../../common/StudentNameLink/StudentNameLink';
import { MaskedStudentData } from '../../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../../analytics/dataMasking';
import { MaskedInput } from '../../MaskedData/MaskedInput';
import { InputText } from 'primereact/inputtext';

const ProgressStudentSnapshotTable: FC = () => {
  const [values, setValues] = useState<ProgressTableStudentsValue[]>([]);
  const [fetchKey, setFetchKey] = useState<number>(0);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [fetchKeyScroll, setFetchKeyScroll] = useState<number>(0);
  const [lazyLoading, setLazyLoading] = useState<boolean>(true);
  const [lastCall, setLastCall] = useState<boolean>(false);
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const userRedux = useAppSelector(selectUser);

  const [studentSkillProgressDetailInput, setStudentSkillProgressDetailInput] = useState<{
    studentId: string;
    skillResult: SkillResult;
    count: number;
  }>();
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const onScroll = (event: UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (!lastCall && scrollTop + clientHeight >= scrollHeight) {
      setLazyLoading(true);
      setFetchKeyScroll(prev => prev + 1);
    }
  };

  return (
    <>
      <Suspense>
        <QueryStudentsProgressReport
          {...{
            fetchingData,
            fetchKey,
            firstLoad,
            setFetchingData,
            setFetchKey,
            setFirstLoad,
            setValues,
          }}
        />
      </Suspense>
      <Suspense>
        <QueryStudentsProgressReportSkills
          {...{
            fetchKeyScroll,
            setLastCall,
            lazyLoading,
            pageInfo,
            setLazyLoading,
            setPageInfo,
            setSkills,
            studentSkillProgressDetailInput,
          }}
        />
      </Suspense>
      {fetchingData ? (
        <ThreeDotLoader dark />
      ) : (
        <DataTable
          sortField="studentLastName"
          sortOrder={1}
          stripedRows
          totalRecords={values.length}
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          filterDisplay="row"
          paginator
          paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          value={values}
        >
          <Column
            field="studentFirstName"
            header="First Name"
            sortable
            filter
            showFilterMenu={false}
            style={{ width: '180px' }}
            filterElement={(option: ColumnFilterElementTemplateOptions) => (
              <MaskedInput>
                <InputText
                  value={option.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    option.filterApplyCallback(e.target.value, option.index)
                  }
                  placeholder="Search"
                  className="p-column-filter"
                />
              </MaskedInput>
            )}
            body={(data: ProgressTableStudentsValue) => (
              <div style={{ fontFamily: 'Poppins' }}>
                <StudentNameLink studentId={data.studentId} classIds={reportsFilterRedux.classes.map(cl => cl.id)}>
                  <MaskedStudentData student={{ publicUserId: data.studentPublicUserId }} type={PiiTypes.FirstName}>
                    {data.studentFirstName}
                  </MaskedStudentData>
                </StudentNameLink>
              </div>
            )}
          />
          <Column
            field="studentLastName"
            header="Last Name"
            sortable
            filter
            showFilterMenu={false}
            filterPlaceholder="Search"
            style={{ width: '180px' }}
            filterElement={(option: ColumnFilterElementTemplateOptions) => (
              <MaskedInput>
                <InputText
                  value={option.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    option.filterApplyCallback(e.target.value, option.index)
                  }
                  placeholder="Search"
                  className="p-column-filter"
                />
              </MaskedInput>
            )}
            body={(data: ProgressTableStudentsValue) => {
              return <ProgressNameColumn studentData={data} filterRoadmapId={reportsFilterRedux.roadmaps.id} />;
            }}
          />
          <Column
            field="lastLogin"
            sortable
            header="Last Login"
            body={(data: ProgressTableStudentsValue) => {
              if (data.lastLogin) {
                return (
                  <span style={{ fontFamily: 'Poppins', whiteSpace: 'nowrap' }}>
                    {formatDateWithTimezone(
                      data?.lastLogin || '',
                      userRedux?.activeTimeZone || '',
                      'dd-MM-yyyy - HH:mm',
                    )}
                  </span>
                );
              }
            }}
          />
          <Column
            field="usersBySkillResults"
            header="Roadmap Progress"
            className="nc_report-progress-bar"
            body={(data: ProgressTableStudentsValue) => {
              const valuesProgress: ProgressBar[] = progressBarDashboardStudentTab(
                data?.studentSkillsResults,
                data?.studentId,
              );
              if (data?.studentSkillsResults?.every(skill => skill.value === 0)) return null;
              return (
                <div key={data.studentId} id="snapshotprogressbar">
                  <SingleUnitProgressBarStudentsProgressTable
                    values={valuesProgress}
                    setStudentSkillProgressDetailInput={setStudentSkillProgressDetailInput}
                    list={skills}
                    onScroll={onScroll}
                    lazyLoading={lazyLoading}
                    setSkills={setSkills}
                    setLazyLoading={setLazyLoading}
                    studentSkillProgressDetailInput={studentSkillProgressDetailInput}
                    setPageInfo={setPageInfo}
                  />
                </div>
              );
            }}
          />
          <Column
            field="roadMapPercentage"
            sortable
            header="% of Roadmap Completed"
            body={(data: ProgressTableStudentsValue) => {
              return (
                <div className="flex justify-content-center">
                  <span style={{ fontFamily: 'Poppins' }}>{data.roadMapPercentage}%</span>
                </div>
              );
            }}
          />
        </DataTable>
      )}
    </>
  );
};

export default ProgressStudentSnapshotTable;
